import { paths } from 'shared/routing/paths';
import { BLACKLISTED_COUNTRY_NAMES } from 'domains/experts/model/blacklistedCountries';

export interface OnboardingHelpItem {
  question: string;
  answer: string;
  /** Optional in-app link rendered after the answer. */
  link?: { label: string; to: string };
  /** Renders a button that opens the support panel's message form. */
  contactSupport?: boolean;
}

/**
 * The per-step "common questions" shown beside each onboarding step: aligned's
 * `expert_onboarding_layout/widgets/*_info.dart` panels (`ksExpertOnboardingInfo*`
 * in `app_strings.dart`).
 *
 * Wording follows upstream except where upstream's answer is not true of this
 * app: legal name, date of birth, and country are read-only in Settings here;
 * blacklisted countries are listed (with a blocking banner) rather than hidden;
 * and a declined identity check offers no retry. The phone-step entry is not
 * ported because this app has no phone step.
 */
export const ONBOARDING_HELP: Record<string, OnboardingHelpItem[]> = {
  [paths.onboarding.root]: [
    {
      question: 'How do you use this information?',
      answer:
        'The information you provide is used to verify your identity and your eligibility to work with clients. All of your information is private and secure and is never sold or used for advertising.',
    },
    {
      question: 'Why do I need to use my legal name?',
      answer:
        'Your legal name, date of birth, and country of residence must match the government-issued photo ID you use to verify your identity in the last step. If they don’t match, verification can fail. If you go by a different name, add it as your preferred first name.',
    },
    {
      question: 'Can I list a country of residence for which I do not have a valid government ID?',
      answer:
        'No, your legal name and country of residence must match the government-issued photo ID you use to verify your identity in a later step.',
    },
    {
      question: 'Which countries are supported?',
      answer: `At Aligned, we support experts from over 200 countries and territories worldwide. Due to current regulatory requirements, we are temporarily unable to support experts in the following regions: ${BLACKLISTED_COUNTRY_NAMES.join(', ')}.`,
    },
    {
      question: 'Can I change this information later?',
      answer: `You can change your preferred first name and gender identity anytime in Settings. Once you complete onboarding, you will need to contact support to change your legal name, date of birth, or country of residence.`,
    },
  ],
  [paths.onboarding.expertise]: [
    {
      question: 'How do you use this information?',
      answer:
        'We use this information to identify your specific areas of expertise to best match you to projects.',
    },
    {
      question: 'How can I best set myself up for success on this step?',
      answer:
        'The easier you make it for our team to understand your expertise and credentials, the faster your expertise will be approved. Be clear and detailed. If you leave out something important, you may miss out on jobs that otherwise match your expertise.',
    },
    {
      question: 'Why do you need to know my language skills?',
      answer:
        'Our partners often have jobs in languages other than English. If you can read and write fluently in any other languages, you will have a larger pool of paid jobs.',
    },
    {
      question: 'Can I change this information later?',
      answer: 'Yes, you can update this information anytime in Settings.',
    },
  ],
  [paths.onboarding.hourlyRate]: [
    {
      question: 'Why do you need my hourly rate?',
      answer:
        'At Aligned, we believe that all experts should be compensated fairly for their expertise. By setting your desired hourly rate, we ensure that you are only assigned to projects that are worth your time and effort.',
    },
    {
      question: 'How do I know what rate to set?',
      answer:
        'The hourly rates for Aligned experts range widely based on domain of expertise, experience, and location. As a new expert, we recommend entering the lowest hourly rate at which you would be happy to work, and adjusting your rate as you get feedback on your performance. This gives you the best chance to qualify for active and upcoming projects. If you learned about Aligned from a job posting, you can use the pay range on the job description as a starting point for your rate.',
    },
    {
      question: 'Can I change my rate later?',
      answer: 'Yes, you can change your rate anytime in Settings.',
    },
  ],
  [paths.onboarding.idVerification]: [
    {
      question: 'Why do you need to verify my identity?',
      answer:
        'Our clients (and many national governments) require us to complete ID verification for all experts on our platform. There are no workarounds and we’re unable to evaluate or assign any experts to paid projects until that step is completed.',
    },
    {
      question: 'What if my ID does not match the information I provided in the previous steps?',
      answer:
        'Go back to the Account step and update your legal name, date of birth, and country of residence to match your government-issued ID before you start verification.',
      link: { label: 'Go to the Account step', to: paths.onboarding.root },
    },
    {
      question: 'What do I do if I’m having trouble getting my selfie verified?',
      answer:
        'First, make sure you have turned off any virtual backgrounds or filters on your camera, as these will cause the verification to fail. Remove your hat and glasses and make sure your face is well lit. Make sure your background is clear and not cluttered.',
    },
    {
      question: 'What do I do if I failed the identity verification?',
      answer:
        'If you failed the ID verification step, your account is now locked and you will be unable to proceed with the sign up process. If you believe there was a mistake verifying your ID, please contact support.',
      contactSupport: true,
    },
  ],
};
